import { ChoiceList, TextField } from "@cmsgov/ds-medicare-gov";
import { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router";
import { ErrorKeys } from "../../common/classes/schema";
import BackNav from "../../common/components/BackNav";
import Div from "../../common/components/div";
import { FullPageSpinner } from "../../common/components/FullPageSpinner";
import H1 from "../../common/components/h1";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import capitalizeFirstLetter from "../../common/functions/capitalizeFirstLetter";
import { getMfaDuplicateChannelErrorKey, hasErrorMessage } from "../../common/functions/getApiErrorMessage";
import translate from "../../common/functions/translate";
import { useMfaInlineError, useMfaPageView } from "../../common/hooks/use.analytics";
import useCheckForSession from "../../common/hooks/useCheckForSession";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { SubmitButton } from "../buttons/Buttons";
import { ConfiguredMfaOption, MfaEnrollStep1Response, SecurityCodeMethod, useMfaApi } from "../hooks/useMfaApi";
import { validateEmail, validatePhone } from "../validators/mfaSchema";
import { VerifyPassword } from "../VerifyPassword";
import { Pill } from "../../common/components/Pill";
import formatMfaChannel from "../../common/functions/formatMfaChannel";
import getLocale from "../../common/functions/getLocale";
import { mfaPageView } from "../../common/analytics";

export const UpdateMfa = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [updateActionType, setUpdateActionType] = useState<'change' | 'remove' | null>(null);
    const [newValue, setNewValue] = useState('');
    const [newValueRef, setNewValueRef] = useState<HTMLElement | null>(null);
    const [inputErr, setInputErr] = useState<ErrorKeys>(null);
    const [apiResponse, setApiResponse] = useState<AxiosResponse<MfaEnrollStep1Response> | null>(null);
    const [isCheckingCookie, setIsCheckingCookie] = useState(true);
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [allOptions, setAllOptions] = useState<ConfiguredMfaOption[] | null>(null);
    const [currentOption, setCurrentOption] = useState<ConfiguredMfaOption | null>(null);

    const history = useHistory();
    const api = useMfaApi();
    const { uuid } = useParams<{uuid: string}>();
    const [isVerified, setIsVerified] = useState(true);
    const type: SecurityCodeMethod | undefined = currentOption?.type;
    const value = currentOption?.mfa_channel || '';

    usePageTitle(translate('mfa_title_edit'));
    useCheckForSession(setIsCheckingSession);
    const newValueInputLabel = type ?  translate(`update_mfa_change_label_${type}`) : '';
    const inlineErr = useMfaInlineError(newValueInputLabel);

    const pageViewSent = useRef(false);
    useEffect(() => {
        if (!isVerified || !type) {
            return;
        }
        if (pageViewSent.current) {
            return;
        }
        pageViewSent.current = true;
        mfaPageView(`my account: mfa: ${type} remove or change`)
    }, [isVerified, type]);

    // After we have validated our session, load MFA options
    const loadRequestMade = useRef(false);
    useEffect(() => {
        if (isCheckingSession || !uuid) {
            return;
        }
        if (loadRequestMade.current) {
            return;
        }
        loadRequestMade.current = true;

        const loadOptions = async () => {
            let options: ConfiguredMfaOption[] = [];
            const res = await api.mfaGetOptions();
            if (res?.data.status === 'ok') {
                options = res.data.configured_options;
                const matchingOption = options.find(x => x.id === uuid) || null;
                setCurrentOption(matchingOption);
            }
            setAllOptions(options);
        }
        loadOptions();
    }, [isCheckingSession, api, uuid]);

    // After session check and MFA options are done, check update-account cookie
    const cookieRequestMade = useRef(false);
    useEffect(() => {
        if (!allOptions) { // null until session check and loading MFA options is done
            return;
        }
        if (cookieRequestMade.current) {
            return;
        }
        cookieRequestMade.current = true;
        const verifyUpdateCookie = async () => {
            const verifyResponse = await api.mfaVerifyCookie();
            if (verifyResponse?.status !== 200) {
                setIsVerified(false);
            }
            setIsCheckingCookie(false);
        }
        verifyUpdateCookie();

    }, [allOptions, api]);


    useEffect(() => {
        if (!type) {
            return;
        }
        let err: ErrorKeys;
        if (type === 'email') {
            err = validateEmail(newValue);
        } else {
            err = validatePhone(newValue, type);
        }
        setInputErr(err);
    }, [newValue, type]);

    async function onSubmit(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        setSubmitAttempted(true);
        if (inputErr && updateActionType !== 'remove') {
            inlineErr.validationError(getInputErrorMessage() || '');
            return;
        }
        setIsLoading(true);
        if (updateActionType === 'change' && type) {
            await changeAndRedirect();
            return;
        }
        if (updateActionType === 'remove') {
            history.push(`/security-code/remove-security-method/${uuid}`, {
                type,
                value,
                updateActionType,
                isMultipleMethods: Boolean(allOptions && allOptions.length > 1)
            });
        }
    }

    async function changeAndRedirect() {
        if (await enrollMethod() && await deleteMethod()) {
            history.push("/security-code/enroll/confirm-your-method", {
                type,
                value: newValue,
                flow: 'edit'
            });
        }
    }

    async function enrollMethod() {
        if (!type) {
            return;
        }
        setIsLoading(true);
        const mfaStepResponse = await api.mfaEnrollStep1({
            type,
            value: newValue
        });
        setIsLoading(false);
        const code = mfaStepResponse?.data.code;
        setApiResponse(mfaStepResponse);
        if (code === 401) {
            setIsVerified(false);
        }

        const errors = mfaStepResponse?.data.errors || [];

        const isPhone = type === 'sms' || type === 'voice';
        if (isPhone && code === 400 && hasErrorMessage(errors, 'MFA_NON_US_PHONE_NUMBER')) {
            setInputErr([`non_us_number_${type}`]);
        } else {
            // set inline error for duplicate channel if applicable
            const duplicateErr = getMfaDuplicateChannelErrorKey(errors, type);
            if (duplicateErr) {
                setInputErr([duplicateErr])
            }
        }

        if (code !== 201) {
            return;
        }
        return 'enrolled';
    }

    async function deleteMethod() {
        setIsLoading(true);
        const removeMethodResponse = await api.mfaDelete(uuid);
        setIsLoading(false);
        setApiResponse(removeMethodResponse);
        if (removeMethodResponse?.data.code === 401) {
            setIsVerified(false);
        }
        if (removeMethodResponse?.status === 204) {
            return 'deleted';
        }
    }

    const removeLabel = <>{translate('remove')} <span className="ds-u-font-weight--bold">{formatMfaChannel(value.toUpperCase())}</span> </>;

    function getInputErrorMessage() {
        if (!submitAttempted || updateActionType === 'remove') {
            return;
        }

        if (inputErr) {
            return translate(inputErr[0]);
        }
    }

    // if no uuid, immediately redirect
    if (!uuid) {
        return <Redirect to={`/my/account-settings?lang=${getLocale()}`}/>;
    }

    // Loading spinner while checking session, getting MFA options, and verifying update-account cookie
    if (isCheckingSession || isCheckingCookie) {
        return <FullPageSpinner />;
    }

    // If no options found, or no matching option, redirect
    const noOptions = allOptions && !allOptions.length;
    if (noOptions || !type) { // type will be set when there is a matching option found
        return <Redirect to={`/my/account-settings?lang=${getLocale()}`}/>
    }

    if (!isVerified) {
        return <VerifyPassword type={type} flow={'edit'} onVerified={() => setIsVerified(true)}/>;
    }

    const errorFlow = `MfaEnrollStep1${capitalizeFirstLetter(type)}` as const;

    const newChannelInput = <TextField
        size={type === 'email' ? undefined : 'medium'}
        labelClassName="ds-u-margin-top--1"
        className={`ds-u-margin-bottom--3 ds-u-margin-left--5`}
        aria-invalid={Boolean(getInputErrorMessage())}
        errorMessage={getInputErrorMessage()}
        hint={translate(`input_hint_${type}`)}
        inputRef={setNewValueRef}
        label={translate(`update_mfa_add_label_${type}`)}
        name={'emailAddress'}
        onChange={event => setNewValue(event.currentTarget.value)}
        value={newValue}
    />;

    const headingTitle = translate('what_would_you_like_to_do');
    return (<>
        <BackNav />
        <Div isContainer paddingy={1} paddingyMD={2}>
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1>
                            {type && translate(`mfa_title_edit_${type}`)}
                        </H1>
                    </Div>
                    <Div cols={12} colsMD={8}>
                        <SmartErrorAlert
                            name={errorFlow}
                            fieldRef={null}
                            apiResponse={apiResponse}
                            conversion={null}
                            invalidFields={null}
                            onInvalidField={() => {}}
                            parentHeading={headingTitle}
                        />
                        {value && type && <div>
                            <p className="ds-u-margin-bottom--3 ds-u-margin-top--3 ds-text-heading--md ds-u-font-weight--bold">{translate(`current_mfa_${type}`)}</p>
                            <Pill>{formatMfaChannel(value)}</Pill>
                        </div>}
                        <ChoiceList
                            label={
                                <h2 className={"ds-u-margin-bottom--1"} style={{ textTransform: 'none' }}>
                                    {translate('what_would_you_like_to_do')}
                                </h2>
                            }
                            labelClassName={"ds-u-measure--wide"}
                            className={'ds-u-margin-bottom--3'}
                            name={'selectSecurityMethod'}
                            onChange={(e) => setUpdateActionType(e.target.value)}
                            type={'radio'}
                            choices={[
                                {
                                    label: translate(`update_mfa_change_label_${type}`),
                                    value: 'change',
                                    hint: translate(`update_mfa_change_hint_${type}`),
                                    checkedChildren: newChannelInput
                                },
                                {
                                    label: removeLabel,
                                    value: 'remove',
                                    hint: translate(`update_mfa_remove_hint_${type!}`)
                                }
                            ]}
                        />
                        <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                            text: translate('continue'),
                            parentComponentHeading: headingTitle,
                        }}>
                            {translate('continue')}
                        </SubmitButton>
                    </Div>
                </Div>
            </form>
        </Div>
    </>);
}